import { generateCatapultAuthUrl } from "@mirage/service-auth/generateCatapultAuthUrl";
import { tagged } from "@mirage/service-logging";
import type { RouteToSlug } from "@mirage/shared/fss-bridge/bridgeRouting";
import {
  getDeepLinkPath,
  getFallbackDeepLinkUrl,
} from "@mirage/shared/fss-bridge/bridgeRouting";
import { useCallback, useEffect, useState } from "react";

const logger = tagged("useGetDeepLinkUrl");

/**
 * Generates a deep link URL that also authenticates the current user. Since most deep links
 * will require opening a new tab, we need to make sure the auth url is synchronously available
 * to any components that use this url, otherwise the browser may block the pop-up. This hook
 * proactively caches the auth url when the consuming component mounts and then returns a function
 * that gets the currently cached auth url and refreshes the cache. Note, if we cannot successfully
 * generate the auth url, we will fallback to using the `/login` route.
 *
 * @param slug - The main route to deep link to.
 * @param params - The query string params to append to deep link.
 * @returns A function that can be used to get a deep link URL for the given slug and params.
 */
export const useGetDeepLinkUrl = (
  slug: RouteToSlug,
  params?: Record<string, string>,
) => {
  const [authUrl, setAuthUrl] = useState<string>(
    getFallbackDeepLinkUrl(slug, params),
  );

  const refreshAuthUrl = useCallback(async () => {
    try {
      setAuthUrl(getFallbackDeepLinkUrl(slug, params));
      const newAuthUrl = await generateCatapultAuthUrl(
        getDeepLinkPath(slug, params),
      );
      if (newAuthUrl) {
        setAuthUrl(newAuthUrl);
      }
    } catch (error) {
      logger.error("Error generating login to path URL", error);
      setAuthUrl(getFallbackDeepLinkUrl(slug, params));
    }
  }, [slug, params]);

  useEffect(() => {
    refreshAuthUrl();
  }, [refreshAuthUrl]);

  // An auth URL is only valid for one use, so we use a callback here to fetch
  // the current auth URL so that we can then immediately refresh it.
  return useCallback(() => {
    const currentAuthUrl = authUrl;
    refreshAuthUrl();
    return currentAuthUrl;
  }, [authUrl, refreshAuthUrl]);
};
